<div class="fonb-shell">

  <div class="fonb-header">
    <div>
      <h2 class="fonb-header__title">{{ 'FLEXI_ONBOARDING.TITLE' | translate }}</h2>
      <p class="fonb-header__meta">{{ 'FLEXI_ONBOARDING.CUSTOMER.HEADER_META' | translate }}</p>
    </div>
    <div class="fonb-header__status is-completed" *ngIf="isCompleted">
      <i class="pi pi-check-circle"></i>
      <span>{{ 'FLEXI_ONBOARDING.STATUS.PUBLISHED' | translate }}</span>
    </div>
  </div>

  <div class="fonb-banner fonb-banner--warning" *ngIf="!isCompleted">
    <i class="pi pi-lock"></i>
    <div>{{ 'FLEXI_ONBOARDING.CUSTOMER.LOCKED_BANNER' | translate }}</div>
  </div>

  <!-- Inline error from the download attempt -->
  <div class="fonb-banner fonb-banner--error" *ngIf="errorMessage">
    <i class="pi pi-exclamation-triangle"></i>
    <div>{{ errorMessage }}</div>
    <button type="button" class="fonb-banner__action" (click)="errorMessage = null">
      {{ 'FLEXI_ONBOARDING.DISMISS' | translate }}
    </button>
  </div>

  <ng-container *ngIf="isCompleted">
    <!-- ============================================================
         COVER METADATA BLOCK — turns the doc from a templated page
         into a record of consultation. Identifies the parties, the
         dates, the validity window, and a stable case ID inspectors
         can cross-reference.
         ============================================================ -->
    <section class="fonb-cover" *ngIf="meeting as m">
      <h3 class="fonb-cover__title">{{ 'FLEXI_ONBOARDING.COVER.TITLE' | translate }}</h3>
      <dl class="fonb-cover__grid">
        <div class="fonb-cover__item">
          <dt>{{ 'FLEXI_ONBOARDING.PDF.BUSINESS' | translate }}</dt>
          <dd>{{ m.businessName }}</dd>
        </div>
        <div class="fonb-cover__item" *ngIf="m.organisationNumber">
          <dt>{{ 'FLEXI_ONBOARDING.PDF.ORG_NUMBER' | translate }}</dt>
          <dd>{{ m.organisationNumber }}</dd>
        </div>
        <div class="fonb-cover__item" *ngIf="m.advisorName">
          <dt>{{ 'FLEXI_ONBOARDING.PDF.ADVISOR' | translate }}</dt>
          <dd>{{ m.advisorName }}</dd>
        </div>
        <div class="fonb-cover__item" *ngIf="m.completedOn">
          <dt>{{ 'FLEXI_ONBOARDING.PDF.CONSULTATION_DATE' | translate }}</dt>
          <dd>{{ m.completedOn | date:'dd-MM-yyyy' }}</dd>
        </div>
        <div class="fonb-cover__item" *ngIf="m.validUntil">
          <dt>{{ 'FLEXI_ONBOARDING.PDF.VALID_UNTIL' | translate }}</dt>
          <dd>{{ m.validUntil }}</dd>
        </div>
        <div class="fonb-cover__item" *ngIf="m.agreementId">
          <dt>{{ 'FLEXI_ONBOARDING.PDF.CASE_ID' | translate }}</dt>
          <dd>BHT-{{ m.businessId }}-{{ m.agreementId }}</dd>
        </div>
      </dl>
    </section>

    <div class="fonb-customer__toolbar">
      <div class="fonb-customer__toolbar-info">
        <i class="pi pi-file-pdf"></i>
        <span>{{ 'FLEXI_ONBOARDING.CUSTOMER.DOWNLOAD_HINT' | translate }}</span>
      </div>
      <button type="button" class="fonb-btn fonb-btn--primary"
              (click)="downloadFinalViewPdf()"
              [disabled]="downloading">
        <i class="pi" [ngClass]="downloading ? 'pi-spin pi-spinner' : 'pi-download'"></i>
        {{ 'FLEXI_ONBOARDING.CUSTOMER.DOWNLOAD_PDF' | translate }}
      </button>
    </div>

    <!-- ============================================================
         AGREED-ACTIONS APPENDIX — top-of-doc index of every Action-
         type public comment, with source topic, owner, and deadline.
         Renders only when at least one action exists.
         ============================================================ -->
    <section class="fonb-actions" *ngIf="actions.length">
      <header class="fonb-actions__head">
        <h3>{{ 'FLEXI_ONBOARDING.PDF.ACTIONS_TITLE' | translate }}</h3>
        <p>{{ 'FLEXI_ONBOARDING.PDF.ACTIONS_INTRO' | translate }}</p>
      </header>
      <ol class="fonb-actions__list">
        <li class="fonb-actions__item" *ngFor="let a of actions">
          <div class="fonb-actions__source">
            <span class="fonb-actions__source-label">{{ 'FLEXI_ONBOARDING.PDF.TOPIC' | translate }} {{ a.stepPosition + 1 }}</span>
            <span class="fonb-actions__source-title">{{ a.stepTitle }}</span>
          </div>
          <p class="fonb-actions__text">{{ a.commentText }}</p>
          <div class="fonb-actions__meta">
            <span class="fonb-actions__chip">
              <i class="pi pi-user"></i>
              {{ 'FLEXI_ONBOARDING.RESPONSIBLE.LABEL' | translate }}: {{ responsibleLabel(a.responsibleParty) }}
            </span>
            <span class="fonb-actions__chip" *ngIf="a.deadline">
              <i class="pi pi-calendar"></i>
              {{ 'FLEXI_ONBOARDING.DEADLINE' | translate }}: {{ a.deadline }}
            </span>
            <span class="fonb-actions__chip fonb-actions__chip--muted" *ngIf="a.createdByName">
              <i class="pi pi-pencil"></i>
              {{ 'FLEXI_ONBOARDING.PDF.LOGGED_BY' | translate }}: {{ a.createdByName }}
            </span>
          </div>
        </li>
      </ol>
    </section>

    <article class="fonb-final" *ngFor="let s of steps; let i = index">
      <h4 class="fonb-final__title">
        <span class="fonb-final__num">{{ i + 1 }}</span>
        <span class="fonb-final__topic">
          {{ 'FLEXI_ONBOARDING.PDF.TOPIC' | translate }} {{ i + 1 }} — {{ s.title }}
          <small class="fonb-final__reviewed" *ngIf="meeting?.completedOn">
            · {{ 'FLEXI_ONBOARDING.PDF.REVIEWED' | translate }} {{ meeting!.completedOn | date:'dd-MM-yyyy' }}
          </small>
        </span>
      </h4>
      <div class="fonb-final__body" [innerHTML]="s.finalText"></div>
    </article>

    <!-- ============================================================
         ATTESTATION BLOCK — closes the document on behalf of the
         AT-2303 approved OHS service. Mirrors the PDF footer.
         ============================================================ -->
    <section class="fonb-attestation" *ngIf="meeting">
      <h4>{{ 'FLEXI_ONBOARDING.PDF.ATTESTATION_TITLE' | translate }}</h4>
      <p>
        {{ 'FLEXI_ONBOARDING.PDF.ATTESTATION_BODY' | translate:{
              business: meeting.businessName,
              advisor: meeting.advisorName || '—',
              date: (meeting.completedOn | date:'dd-MM-yyyy') || ''
            } }}
      </p>
    </section>
  </ng-container>

</div>
